import { ComponentType, forwardRef, useEffect, useRef, useState, type FC } from 'react';
import { ArrowDownIcon } from 'lucide-react';
import Composer from './composer';

import { TooltipIconButton, TooltipIconButtonProps } from './tooltip-icon-button';

import {
    EditComposer,
    // UserMessage,
    ThreadConfig,
    ThreadConfigProvider,
    ThreadConfigProviderProps,
    useThreadConfig,
} from '@assistant-ui/react-ui';

import { ThreadPrimitive, useThread } from '@assistant-ui/react';
import { withDefaults } from './utils/with-defaults';
import AssistantMessage from './assistant-message';
import { UserMessage } from './user-message';
import CustomThreadWelcome from './thread-welcome';
import { useLocation, useParams } from 'react-router-dom';
import { paths } from '../../../../../routes/paths';

const Thread: FC<ThreadConfig & { memory?: boolean }> = ({ memory, ...config }) => {
    // Start copied solution for auto-scrolling to bottom of thread when a new message is added
    // https://github.com/assistant-ui/assistant-ui/issues/1916
    const isRunning = useThread((thread) => thread.isRunning);
    const [autoScroll, setAutoScroll] = useState(true);
    const lastScrollTop = useRef<number>(0);
    const { threadId } = useParams();

    useEffect(() => {
        setAutoScroll(true);
    }, [isRunning]);

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const target = event.target as HTMLDivElement;

        if (isRunning && lastScrollTop.current > target.scrollTop) {
            setAutoScroll(false);
        }

        lastScrollTop.current = target.scrollTop;
    };
    // End copied solution for auto-scrolling to bottom of thread when a new message is added

    const location = useLocation();
    const navigatedFrom = location.state?.from;
    // Only animate when coming from home AND creating a new thread (no existing threadId in state)
    const transitionComposer = navigatedFrom === paths.NEW.HOME && !location.state?.existingThread;
    const {
        components: {
            Composer: ComposerComponent = Composer,
            ThreadWelcome: ThreadWelcomeComponent = CustomThreadWelcome,
            MessagesFooter,
            ...messageComponents
        } = {},
    } = config;

    return (
        <ThreadRoot config={config} className="bg-background flex flex-col box-border relative w-full h-full">
            <ThreadViewport autoScroll={autoScroll} onScroll={handleScroll}>
                <ThreadWelcomeComponent />
                <ThreadMessages MessagesFooter={MessagesFooter} components={messageComponents} />
                <ThreadFollowupSuggestions />
                <ThreadViewportFooter>
                    <ThreadScrollToBottom onClick={() => setAutoScroll(true)} />
                    <ThreadPrimitive.If empty={false}>
                        <ComposerComponent withAnimation={transitionComposer} />
                    </ThreadPrimitive.If>
                </ThreadViewportFooter>
                <div className="absolute -bottom-1 left-0 right-0 w-full min-h-16 pointer-events-none bg-linear-to-t from-background-primary to-transparent blur-xs z-40" />
            </ThreadViewport>
        </ThreadRoot>
    );
};

namespace ThreadRoot {
    export type Element = HTMLDivElement;
    export type Props = ThreadPrimitive.Root.Props & ThreadConfigProviderProps;
}

const ThreadRootStyled = withDefaults(ThreadPrimitive.Root, {
    className: 'aui-root aui-thread-root self-center',
});

const ThreadRoot = forwardRef<ThreadRoot.Element, ThreadRoot.Props>(({ config, ...props }, ref) => {
    return (
        <ThreadConfigProvider config={config}>
            <ThreadRootStyled {...props} ref={ref} />
        </ThreadConfigProvider>
    );
});

ThreadRoot.displayName = 'ThreadRoot';

const ThreadViewport = withDefaults(ThreadPrimitive.Viewport, {
    className: 'aui-thread-viewport overflow-x-hidden w-full',
});

ThreadViewport.displayName = 'ThreadViewport';

const ThreadViewportFooter = withDefaults('div', {
    className: 'aui-thread-viewport-footer z-50 bg-transparent',
});

ThreadViewportFooter.displayName = 'ThreadViewportFooter';

const ThreadMessages: FC<{
    unstable_flexGrowDiv?: boolean;
    components?: Partial<ThreadPrimitive.Messages.Props['components']>;
    MessagesFooter?: ComponentType | undefined;
}> = ({ components, MessagesFooter, unstable_flexGrowDiv: flexGrowDiv = true, ...rest }) => {
    const thread = useThread();
    const messages = thread.messages;
    const lastContentPart = messages[messages.length - 1]?.content[messages[messages.length - 1].content.length - 1];
    const showRunningDiv = thread.isRunning && lastContentPart?.type && lastContentPart?.type !== 'text';
    return (
        <>
            <ThreadPrimitive.Messages
                components={{
                    ...components,
                    UserMessage: components?.UserMessage ?? UserMessage,
                    AssistantMessage: components?.AssistantMessage ?? AssistantMessage,
                    EditComposer: components?.EditComposer ?? EditComposer,
                }}
                {...rest}
            />
            {MessagesFooter && <MessagesFooter />}
            {flexGrowDiv && (
                <ThreadPrimitive.If empty={false}>
                    <div
                        data-status={showRunningDiv ? 'running' : 'complete'}
                        className="grow aui-md w-full px-2 -mt-2 max-w-[calc(var(--thread-max-width)*0.85)]"
                    />
                </ThreadPrimitive.If>
            )}
        </>
    );
};

ThreadMessages.displayName = 'ThreadMessages';

const ThreadFollowupSuggestions: FC = () => {
    const suggestions = useThread((t) => {
        // console.log('thread context', t);
        // set this in the mastra runtime provider's suggestions state
        return t.suggestions;
    });

    return (
        <ThreadPrimitive.If empty={false} running={false}>
            <div className="aui-thread-followup-suggestions">
                {suggestions?.map((suggestion, idx) => (
                    <ThreadPrimitive.Suggestion
                        key={idx}
                        className="aui-thread-followup-suggestion"
                        prompt={suggestion.prompt}
                        method="replace"
                        autoSend
                    >
                        {suggestion.prompt}
                    </ThreadPrimitive.Suggestion>
                ))}
            </div>
        </ThreadPrimitive.If>
    );
};

const ThreadScrollToBottomIconButton = withDefaults(TooltipIconButton, {
    variant: 'outline',
    className: 'aui-thread-scroll-to-bottom',
});

namespace ThreadScrollToBottom {
    export type Element = HTMLButtonElement;
    export type Props = Partial<TooltipIconButtonProps>;
}

const ThreadScrollToBottom = forwardRef<ThreadScrollToBottom.Element, ThreadScrollToBottom.Props>((props, ref) => {
    const { strings: { thread: { scrollToBottom: { tooltip = 'Scroll to bottom' } = {} } = {} } = {} } =
        useThreadConfig();
    return (
        <ThreadPrimitive.ScrollToBottom asChild>
            <ThreadScrollToBottomIconButton tooltip={tooltip} {...props} ref={ref}>
                {props.children ?? <ArrowDownIcon />}
            </ThreadScrollToBottomIconButton>
        </ThreadPrimitive.ScrollToBottom>
    );
});

ThreadScrollToBottom.displayName = 'ThreadScrollToBottom';

const exports = {
    Root: ThreadRoot,
    Viewport: ThreadViewport,
    Messages: ThreadMessages,
    FollowupSuggestions: ThreadFollowupSuggestions,
    ScrollToBottom: ThreadScrollToBottom,
    ViewportFooter: ThreadViewportFooter,
};

export default Object.assign(Thread, exports) as typeof Thread & typeof exports;
